import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import PinMarker from './PinMarker';
import PinDetailsModal from './PinDetailsModal';
import AddPinModal from './AddPinModal';
import { MapPin, PinType } from '../models';
import { PinsService, PinSeenService } from '../services';
import { SoundService } from '../services/SoundService';
import { useAuth } from '../context/AuthContext';

// Distance in meters — mirrors CLLocation.distance(from:)
const distanceMeters = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const PROXIMITY_METERS = 100; // matches iOS checkProximityToPin
const RE_NOTIFY_MS = 5 * 60 * 1000; // matches iOS 5-minute re-notify window

interface MapProps {
  showAllPins?: boolean;
  mapType?: 'standard' | 'satellite';
  className?: string;
}

interface MapControllerProps {
  pins: MapPin[];
  businessPins: MapPin[];
  selectedPin: MapPin | null;
  mapType: 'standard' | 'satellite';
  dropMode: boolean;
  seenPins: Set<string>;
  onPinClick: (pin: MapPin) => void;
  onMapClick: (lat: number, lng: number) => void;
}

const MapController: React.FC<MapControllerProps> = ({
  pins,
  businessPins,
  selectedPin,
  mapType,
  dropMode,
  seenPins,
  onPinClick,
  onMapClick
}) => {
  const map = useMap();
  const allPins = [...pins, ...businessPins];

  // Listen for center on location events
  useEffect(() => {
    const handleCenterOnLocation = (event: CustomEvent) => {
      const { lat, lng } = event.detail;
      map.setView([lat, lng], 8);
    };

    window.addEventListener('centerOnLocation', handleCenterOnLocation as EventListener);

    return () => {
      window.removeEventListener('centerOnLocation', handleCenterOnLocation as EventListener);
    };
  }, [map]);

  // Fit map to show St. Kitts & Nevis when no pins or focus on pins
  useEffect(() => {
    // DON'T adjust the view if there are pins - keep the initial zoom of 16
    // This prevents the map from zooming out when pins load
    if (allPins.length > 0) {
      // Just log that pins loaded, don't change the view
      console.log(`Loaded ${allPins.length} pins, keeping zoom at 16`);
    }
  }, [allPins, map]);

  // Center on selected pin
  useEffect(() => {
    if (selectedPin) {
      map.setView([selectedPin.latitude, selectedPin.longitude], 13);
    }
  }, [selectedPin, map]);

  // While in drop mode, a map click chooses the pin location
  useMapEvents({
    click: (e) => {
      if (dropMode) {
        onMapClick(e.latlng.lat, e.latlng.lng);
      }
    }
  });

  // Crosshair cursor while dropping a pin
  useEffect(() => {
    map.getContainer().style.cursor = dropMode ? 'crosshair' : '';
  }, [dropMode, map]);

  return (
    <>
      {allPins.map(pin => (
        <PinMarker
          key={pin.id}
          pin={pin}
          isSeen={seenPins.has(pin.id)}
          onPinClick={onPinClick}
        />
      ))}
    </>
  );
};

const Map: React.FC<MapProps> = ({ showAllPins = false, mapType = 'standard', className }) => {
  const [pins, setPins] = useState<MapPin[]>([]);
  const [businessPins, setBusinessPins] = useState<MapPin[]>([]);
  const [selectedPin, setSelectedPin] = useState<MapPin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const [dropMode, setDropMode] = useState(false);
  const [pendingPinLocation, setPendingPinLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [addPinError, setAddPinError] = useState<string | null>(null);
  const [seenPins, setSeenPins] = useState<Set<string>>(new Set());
  const notifiedPinsRef = React.useRef<Record<string, number>>({});

  // Seen pins — mirrors iOS PinSeenTracker (Firestore when signed in,
  // localStorage for guests)
  useEffect(() => {
    let cancelled = false;
    PinSeenService.loadSeenPins(user?.id ?? null).then(ids => {
      if (!cancelled) setSeenPins(ids);
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    const pinsService = new PinsService({
      onPinsUpdate: (newPins) => {
        setPins(newPins);
        setIsLoading(false);
      },
      onBusinessPinsUpdate: (newBusinessPins) => {
        setBusinessPins(newBusinessPins);
        setIsLoading(false);
      },
      onError: (err) => {
        console.error('Error fetching pins:', err);
        setError('Failed to load pins. Please refresh the page.');
        setIsLoading(false);
      }
    });

    // Start listening for pins
    pinsService.startListening(showAllPins);

    return () => {
      pinsService.destroy();
    };
  }, [showAllPins]);

  // Proximity alerts — mirrors iOS MapViewModel.checkProximityToAllPins:
  // alert once per pin when within 100m, re-notify after 5 minutes
  useEffect(() => {
    if (!('geolocation' in navigator)) return;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        const allPins = [...pins, ...businessPins];
        for (const pin of allPins) {
          const dist = distanceMeters(
            pos.coords.latitude, pos.coords.longitude,
            pin.latitude, pin.longitude
          );
          const lastNotified = notifiedPinsRef.current[pin.id] ?? 0;
          if (dist <= PROXIMITY_METERS && now - lastNotified > RE_NOTIFY_MS) {
            notifiedPinsRef.current[pin.id] = now;
            SoundService.playProximityAlert();
          }
        }
      },
      () => {},
      { enableHighAccuracy: true }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [pins, businessPins]);

  const handlePinClick = (pin: MapPin) => {
    // Mark as seen first so the pulsing ring stops — mirrors iOS tap flow
    if (!seenPins.has(pin.id)) {
      setSeenPins(PinSeenService.markPinAsSeen(pin.id, user?.id ?? null));
    }
    setSelectedPin(pin);
  };

  const handleMapClick = (lat: number, lng: number) => {
    setPendingPinLocation({ lat, lng });
    setDropMode(false);
  };

  const handleAddPin = async (type: PinType, title: string, description: string, hasLiveChat: boolean) => {
    if (!user || !pendingPinLocation) return;
    try {
      await PinsService.addPin(
        pendingPinLocation.lat,
        pendingPinLocation.lng,
        type,
        title,
        description,
        user.id,
        undefined,
        hasLiveChat
      );
      setPendingPinLocation(null);
      setAddPinError(null);
      SoundService.playPinDropped();
    } catch (e) {
      console.error('Failed to add pin:', e);
      setAddPinError('Could not save the pin. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setSelectedPin(null);
  };

  // Choose tile layer based on map type
  const getTileLayer = () => {
    if (mapType === 'satellite') {
      // Use Esri satellite imagery which works reliably
      return (
        <TileLayer
          attribution='&copy; <a href="https://www.arcgis.com/">Esri</a> &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
      );
    } else {
      // OpenStreetMap standard tiles — free, no API key
      return (
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
      );
    }
  };

  if (error) {
    return (
      <div className={`map-container ${className || ''}`}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          backgroundColor: '#f5f5f5',
          color: '#666',
          fontSize: '18px'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`map-container ${className || ''}`} style={{ position: 'relative' }}>
      <MapContainer
        center={[17.22, -62.66]} // Center between St. Kitts & Nevis
        zoom={11}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        attributionControl={true}
        doubleClickZoom={true}
        scrollWheelZoom={true}
        boxZoom={true}
        keyboard={true}
        dragging={true}
        touchZoom={true}
        bounceAtZoomLimits={true}
        maxZoom={18}
        minZoom={5}
      >
        {getTileLayer()}
        <MapController
          pins={pins}
          businessPins={businessPins}
          selectedPin={selectedPin}
          mapType={mapType}
          dropMode={dropMode}
          seenPins={seenPins}
          onPinClick={handlePinClick}
          onMapClick={handleMapClick}
        />
      </MapContainer>

      {/* Drop-a-pin button — mirrors iOS AddPinView flow */}
      {isAuthenticated && (
        <button
          onClick={() => setDropMode(!dropMode)}
          title={dropMode ? 'Cancel pin drop' : 'Drop a pin'}
          style={{
            position: 'absolute', bottom: '24px', right: '16px', zIndex: 1000,
            width: '52px', height: '52px', borderRadius: '50%', border: 'none',
            backgroundColor: dropMode ? '#ff3b30' : '#007aff', color: 'white',
            fontSize: '24px', cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
          }}
        >
          {dropMode ? '✕' : '📌'}
        </button>
      )}
      {dropMode && (
        <div style={{
          position: 'absolute', bottom: '86px', right: '16px', zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.75)', color: 'white',
          padding: '8px 12px', borderRadius: '8px', fontSize: '13px'
        }}>
          Tap the map to drop your pin
        </div>
      )}
      {addPinError && (
        <div style={{
          position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 1000, backgroundColor: '#ff3b30', color: 'white',
          padding: '8px 16px', borderRadius: '8px', fontSize: '13px'
        }}>
          {addPinError}
        </div>
      )}

      {pendingPinLocation && (
        <AddPinModal
          latitude={pendingPinLocation.lat}
          longitude={pendingPinLocation.lng}
          onSubmit={handleAddPin}
          onCancel={() => setPendingPinLocation(null)}
        />
      )}

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span>Loading pins...</span>
        </div>
      )}

      {selectedPin && (
        <PinDetailsModal
          pin={selectedPin}
          isOpen={!!selectedPin}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default Map;
